// src/pages/finance/transaction/TransactionEntry.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const TransactionEntry = () => {
    const [Transactions, setTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);

    // ✅ payment modes for dropdown
    const [paymentModes, setPaymentModes] = useState([]);
    const [isPaymentModesLoading, setIsPaymentModesLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const handleShowFilterModal = () => setShowFilterModal(true);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    // ✅ keep same role logic as your Roles screen
    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

    // ✅ Filter state like Roles
    const [filter, setFilter] = useState({
        from_date: "",
        to_date: "",
        account_id: "",
        txn_type: "",
        payment_mode_id: "", // ✅ dropdown value
        is_active: "",
    });

    const fetchTransactions = async () => {
        try {
            const resp = await axios.get(`${baseUrl}/transactionentry_read/`, {
                params: { school_id: userObj.school_id },
            });
            setTransactions(Array.isArray(resp.data) ? resp.data : []);
        } catch (e) {
            console.error("Error fetching transactions:", e);
            setTransactions([]);
        }
    };

    const fetchAccounts = async () => {
        try {
            const resp = await axios.get(`${baseUrl}/account-head/`, {
                params: { school_id: userObj.school_id },
            });

            setAccounts(
                (Array.isArray(resp.data) ? resp.data : []).filter(
                    (acc) => acc.is_active === "Active"
                )
            );
        } catch (e) {
            setAccounts([]);
        }
    };

    // ✅ fetch payment modes (dropdown)
    const fetchPaymentModes = async () => {
        try {
            setIsPaymentModesLoading(true);
            const resp = await axios.get(`${baseUrl}/payment-modes/`, {
                params: { school_id: userObj.school_id }, // remove if not required
            });
            setPaymentModes(Array.isArray(resp.data) ? resp.data : []);
        } catch (e) {
            console.error("Error fetching payment modes:", e);
            setPaymentModes([]);
        } finally {
            setIsPaymentModesLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        Promise.all([fetchTransactions(), fetchAccounts(), fetchPaymentModes()]).finally(() =>
            setIsLoading(false)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const accountOptions = useMemo(() => {
        return (accounts || []).map((a) => ({
            id: a.account_head_id ?? a.account_id ?? a.node_id ?? a.id,
            name: a.account_head_name ?? a.account_name ?? a.node_name ?? a.name ?? "Account",
        }));
    }, [accounts]);
    const accountSelectOptions = useMemo(() => {
        return (accountOptions || []).map((a) => ({
            value: a.id,
            label: a.name,
        }));
    }, [accountOptions]);

    const selectedAccountOption = useMemo(() => {
        if (!filter.account_id) return null;
        return (
            accountSelectOptions.find((o) => String(o.value) === String(filter.account_id)) || null
        );
    }, [filter.account_id, accountSelectOptions]);
    const paymentModeOptions = useMemo(() => {
        return (paymentModes || []).map((p) => ({
            value: p.payment_mode_id,
            label: p.payment_mode_name,
        }));
    }, [paymentModes]);

    const selectedPaymentModeOption = useMemo(() => {
        if (!filter.payment_mode_id) return null;
        return (
            paymentModeOptions.find((o) => String(o.value) === String(filter.payment_mode_id)) || null
        );
    }, [filter.payment_mode_id, paymentModeOptions]);

    const handleEditClick = (transaction_id) => {
        const record = Transactions.find((r) => r.transaction_id === transaction_id);
        if (record) {
            navigate("/finance/transaction/add", { state: { TransactionData: record } });
        }
    };

    const handleDeleteClick = async (transaction_id) => {
        const confirmDelete = window.confirm("Are you sure you want to change the status?");
        if (!confirmDelete) return;

        try {
            await axios.post(`${baseUrl}/transaction/delete`, {
                transaction_id,
                modified_by: userObj.username || userObj.user_name || "admin",
            });

            toast.success("Record Set to InActive");
            fetchTransactions();
        } catch (e) {
            console.error("Delete error:", e);
            toast.error("Failed to delete record");
        }
    };

    // ✅ Grid columns like Roles (simple, clean)
    const columns = useMemo(
        () => [
            {
                field: "transaction_date",
                headerName: "Date",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => {
                    const formatDate = (date) => {
                        if (!date) return "-";

                        const d = new Date(date);

                        const day = String(d.getDate()).padStart(2, "0");
                        const month = String(d.getMonth() + 1).padStart(2, "0");
                        const year = d.getFullYear();

                        return `${day}-${month}-${year}`;
                    };

                    return (
                        <Tooltip title={params.value || ""}>
                            <span>{formatDate(params.value)}</span>
                        </Tooltip>
                    );
                },
            },
            {
                field: "account_head_name",
                headerName: "Account Head",
                flex: 1.5,
                headerAlign: "center",
                align: "left",
                renderCell: (params) => (
                    <Tooltip title={params.value || ""}>
                        <span>{params.value || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "amount",
                headerName: "Amount",
                flex: 1,
                headerAlign: "center",
                align: "right",
                renderCell: (params) => (
                    <Tooltip title={String(params.value ?? "")}>
                        <span>{params.value ?? "-"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "txn_type",
                headerName: "Transaction Type",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Tooltip title={params.value || ""}>
                        <span>{params.value || "-"}</span>
                    </Tooltip>
                ),
            },

            {
                field: "payment_mode_name",
                headerName: "Payment Mode",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Tooltip title={params.value || ""}>
                        <span>{params.value || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "is_subledger",
                headerName: "Subledger",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Tooltip title={params.value === true ? "Yes" : "No"}>
                        <span>{params.value === true ? "Yes" : "No"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "reference_no",
                headerName: "Ref No",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Tooltip title={params.value || ""}>
                        <span>{params.value || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "description",
                headerName: "Description",
                flex: 2,
                headerAlign: "center",
                align: "left",
                renderCell: (params) => (
                    <Tooltip title={params.value || ""}>
                        <span className="text-truncate" style={{ maxWidth: 250 }}>
                            {params.value || "-"}
                        </span>
                    </Tooltip>
                ),
            },
            {
                field: "is_active",
                headerName: "Status",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Tooltip title={params.value === true ? "Active" : "InActive"}>
                        <span>{params.value === true ? "Active" : "InActive"}</span>
                    </Tooltip>
                ),
            },
            ...(canSubmit
                ? [
                    {
                        field: "actions",
                        headerName: "Actions",
                        flex: 1,
                        headerAlign: "center",
                        align: "center",
                        sortable: false,
                        renderCell: (params) => (
                            <div className="tableActions">
                                <span
                                    className="commonActionIcons"
                                    onClick={() => handleEditClick(params.row.transaction_id)}
                                >
                                    <MdEdit />
                                </span>
                                {/* <span
                                    className="commonActionIcons"
                                    onClick={() => handleDeleteClick(params.row.transaction_id)}
                                >
                                    <MdDelete />
                                </span> */}
                            </div>
                        ),
                    },
                ]
                : []),
        ],
        [canSubmit, Transactions]
    );

    // ✅ Search logic exactly like Roles
    const searchableColumns = [
        (row) => row.transaction_date,
        (row) => row.account_head_name,
        (row) => row.txn_type,
        (row) => row.amount,
        (row) => row.payment_mode_name,
        (row) => row.reference_no,
        (row) => row.description,
        (row) => (row.is_active ? "Active" : "InActive"),
        (row) => (row.is_subledger === true ? "Yes" : "No"),
    ];

    const filteredRecords = (Transactions || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || "").toUpperCase().replace(/[-\s]+/g, "");
            const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, "");
            return stringValue.includes(normalizedQuery);
        })
    );

    const handleSearchChange = (e) => setSearchQuery(e.target.value);

    // ✅ FILTER submit like Roles (modal closes)
    const handleFilterSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            action: "FILTER",
            school_id: userObj.school_id,
            from_date: filter.from_date || null,
            to_date: filter.to_date || null,
            account_id: filter.account_id ? Number(filter.account_id) : 0,
            txn_type: filter.txn_type || "",
            payment_mode_id: filter.payment_mode_id ? Number(filter.payment_mode_id) : 0, // ✅ dropdown
            is_active: filter.is_active === "" ? null : filter.is_active === "true",
        };

        try {
            const resp = await axios.post(`${baseUrl}/transaction/entry`, payload, {
                headers: { "Content-Type": "application/json" },
            });

            const dataArray = Array.isArray(resp.data) ? resp.data : [];
            setTransactions(dataArray);
            setShowFilterModal(false);
        } catch (error) {
            console.error("Filter error:", error);
            toast.error("Failed to filter records");
        }
    };

    // ✅ Reset like Roles
    const handleReset = async (e) => {
        e.preventDefault();
        setFilter({
            from_date: "",
            to_date: "",
            account_id: "",
            txn_type: "",
            payment_mode_id: "", // ✅ reset dropdown
            is_active: "",
        });
        setShowFilterModal(true);
        fetchTransactions();
    };

    return (
        <div className="pageBody">
            <ToastContainer />

            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Transaction Entries</h6>
                    </div>

                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>

                        {canSubmit && (
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                                <Button
                                    className="primaryBtn"
                                    variant="primary"
                                    onClick={() => navigate("/finance/transaction/add")}
                                >
                                    <MdAddCircle />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>
            </div>

            <div className="commonTable">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={filteredRecords.length ? filteredRecords : []}
                            columns={columns}
                            getRowId={(row) => row.transaction_id}
                            pageSizeOptions={[10, 20, 50]}
                            showToolbar={false}
                            pagination
                            density="compact"
                            maxHeight={550}
                        />
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>

                <Modal.Body className="modalBodyScrollable">
                    <Form onSubmit={handleFilterSubmit}>
                        <Row>
                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="from_date">
                                        <Form.Label>From Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filter.from_date}
                                            onChange={(e) => setFilter((f) => ({ ...f, from_date: e.target.value }))}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="to_date">
                                        <Form.Label>To Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filter.to_date}
                                            onChange={(e) => setFilter((f) => ({ ...f, to_date: e.target.value }))}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="account_id">
                                        <Form.Label>Account Head</Form.Label>
                                        <Select
                                            inputId="account_id"
                                            classNamePrefix="react-select"
                                            placeholder="Select Account Head"
                                            isSearchable
                                            isClearable
                                            options={accountSelectOptions}
                                            value={selectedAccountOption}
                                            onChange={(opt) =>
                                                setFilter((f) => ({
                                                    ...f,
                                                    account_id: opt ? opt.value : "",
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="txn_type">
                                        <Form.Label>Transaction Type</Form.Label>
                                        <Form.Select
                                            value={filter.txn_type}
                                            onChange={(e) => setFilter((f) => ({ ...f, txn_type: e.target.value }))}
                                        >
                                            <option value="">Select Transaction Type</option>
                                            <option value="DEBIT">Debit</option>
                                            <option value="CREDIT">Credit</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </Col>

                            {/* ✅ Payment Mode Dropdown */}
                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="payment_mode_id">
                                        <Form.Label>Payment Mode</Form.Label>

                                        <Select
                                            inputId="payment_mode_id"
                                            classNamePrefix="react-select"
                                            placeholder={isPaymentModesLoading ? "Loading..." : "Select Payment Mode"}
                                            isSearchable
                                            isClearable
                                            isLoading={isPaymentModesLoading}
                                            options={paymentModeOptions}
                                            value={selectedPaymentModeOption}
                                            onChange={(opt) =>
                                                setFilter((f) => ({
                                                    ...f,
                                                    payment_mode_id: opt ? opt.value : "",
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>

                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
                        Reset
                    </Button>
                    <div>
                        <Button
                            variant="secondary"
                            className="btn-danger secondaryBtn me-2"
                            onClick={handleCloseFilterModal}
                        >
                            Cancel
                        </Button>
                        <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TransactionEntry;